import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CouponsTable from "./CouponsTable";
import CouponsSummary from "./CouponsSummary";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import WhiteTitledCard from "../../../components/cards/WhiteTitledCard";
import UsedCouponsTable from "./UsedCouponsTable";
import CashbackIssuancesTable from "./CashbackIssuancesTable";
import Button from "../../ui/Button";
import CreateCouponModal from "./modals/CreateCouponModal";
import EditCouponModal from "./modals/EditCoupon";
import CashbackEntryGuideModal from "./modals/CashbackEntryGuideModal";
import { fetchAllCouponsSummary } from "../../../redux/features/CouponSlice";
import { fetchCashbackIssuancesCount } from "../../../redux/features/cashbackSlice";
import { isOppAdmin } from "../../../helpers";

/**
 * Semantic, URL-stable tab keys. We persist these (not the numeric IDs) so:
 *   - the URL is human-readable (`?tab=cashback` vs `?tab=3`),
 *   - tab order can change without breaking bookmarks,
 *   - unknown values fall back to "all" safely.
 */
const TAB_KEY_TO_ID: Record<string, number> = {
  all: 1,
  used: 2,
  cashback: 3,
};
const TAB_ID_TO_KEY: Record<number, string> = {
  1: "all",
  2: "used",
  3: "cashback",
};
const DEFAULT_TAB_ID = 1;

const Coupons = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { coupons, usedCoupons, couponsSummary } = useAppSelector(
    (state) => state.coupons
  );
  const { issuances: cashbackIssuances, totalCount: cashbackTotalCount } =
    useAppSelector((state) => state.cashback);

  const { user: authUser } = useAppSelector((state) => state.auth);
  const oppAssignOnly = isOppAdmin(authUser);

  const activeTab = useMemo(() => {
    const key = searchParams.get("tab");
    if (oppAssignOnly && key === "cashback") return DEFAULT_TAB_ID;
    if (key && TAB_KEY_TO_ID[key]) return TAB_KEY_TO_ID[key];
    return DEFAULT_TAB_ID;
  }, [searchParams, oppAssignOnly]);

  useEffect(() => {
    if (oppAssignOnly && searchParams.get("tab") === "cashback") {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
  }, [oppAssignOnly, searchParams, setSearchParams]);

  const setActiveTab = useCallback(
    (id: number) => {
      if (oppAssignOnly && id === 3) return;
      const key = TAB_ID_TO_KEY[id];
      if (!key) return;
      const next = new URLSearchParams(searchParams);
      // Keep the URL minimal: omit the param entirely when on the default tab.
      if (id === DEFAULT_TAB_ID) {
        next.delete("tab");
      } else {
        next.set("tab", key);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams, oppAssignOnly]
  );

  const [selectedCoupon, setSelectedCoupon] = useState<any>();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [cashbackGuideOpen, setCashbackGuideOpen] = useState(false);
  const [couponsRefreshKey, setCouponsRefreshKey] = useState(0);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const refreshListAndSummary = () => {
    setCouponsRefreshKey((k) => k + 1);
    dispatch(fetchAllCouponsSummary());
  };

  // Prefetch the cashback issuance total once on page mount so the tab badge
  // is accurate before the user opens the Cashback tab. Lives in a dedicated
  // slice field (`totalCount`) — never collides with the table's filtered
  // list state. See cashbackSlice for the TODO that lets us drop this once
  // backend adds the count to the coupons summary endpoint.
  useEffect(() => {
    if (!oppAssignOnly) dispatch(fetchCashbackIssuancesCount());
  }, [dispatch, oppAssignOnly]);

  // Tab counts are FILTER-AWARE — they reflect the count of records that
  // match the user's current filter on each tab. This is the operationally
  // useful number ("how big is the thing I'm about to scan?"). The
  // unfiltered category totals are still surfaced on the summary cards
  // above (Total / Active / Used / Expired).
  //
  // Source preference:
  //   1. The list endpoint's meta.totalItems (filter-aware) once the tab's
  //      table has fetched at least once.
  //   2. Fallback to the unfiltered summary / dedicated count for tabs the
  //      user hasn't opened yet, so badges aren't 0 on first load.
  //
  // The fallbacks are populated upfront via:
  //   - `<CouponsSummary />` (already mounted) for totalCreated / totalUsed.
  //   - `fetchCashbackIssuancesCount()` dispatched in the effect below for
  //      the Cashback tab.
  const tabListRaw = [
    {
      id: 1,
      name: "All coupons",
      count:
        coupons?.data?.meta?.totalItems ??
        couponsSummary?.data?.totalCreated ??
        0,
    },
    {
      id: 2,
      name: "Used",
      count:
        usedCoupons?.data?.meta?.totalItems ??
        couponsSummary?.data?.totalUsed ??
        0,
    },
    ...(oppAssignOnly
      ? []
      : [
          {
            id: 3,
            name: "Cashback Issuances",
            count:
              cashbackIssuances?.meta?.totalItems ?? cashbackTotalCount ?? 0,
          },
        ]),
  ];

  const tabList = tabListRaw;

  return (
    <div className="p-6 flex flex-col gap-10">
      <p className="font-bold text-2xl">Coupons Management</p>
      <CouponsSummary />

      <WhiteTitledCard>
        <div className="mb-4 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="-mx-1 flex max-w-full items-center gap-1 overflow-x-auto overflow-y-hidden pb-1 sm:mx-0 sm:pb-0">
            {tabList?.map((tab) => (
              <div
                key={tab.id}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActiveTab(tab.id);
                  }
                }}
                className={`flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold ${
                  activeTab === tab.id
                    ? "border-b-2 border-primary text-black"
                    : "text-gray-600"
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span>{tab.name}</span>
                <span className="rounded-md bg-lightPurple px-2 text-accent">
                  {tab?.count}
                </span>
              </div>
            ))}
          </div>

          {!oppAssignOnly &&
            (activeTab !== 3 ? (
              <Button
                layout="toolbar"
                size="sm"
                label="Create Coupon"
                onClick={() => setCreateModalOpen(true)}
              />
            ) : (
              <Button
                layout="toolbar"
                size="sm"
                label="Issue Cashback"
                onClick={() => setCashbackGuideOpen(true)}
              />
            ))}
        </div>
        {activeTab === 1 && (
          <CouponsTable
            setSelectedCoupon={setSelectedCoupon}
            selectedCoupon={selectedCoupon}
            setEditModalOpen={setEditModalOpen}
            onCouponsMutated={refreshListAndSummary}
            refreshKey={couponsRefreshKey}
            assignOnly={oppAssignOnly}
          />
        )}
        {activeTab === 2 && <UsedCouponsTable />}
        {activeTab === 3 && !oppAssignOnly && <CashbackIssuancesTable />}
      </WhiteTitledCard>

      {!oppAssignOnly && (
        <>
          <CreateCouponModal
            open={createModalOpen}
            onClose={() => setCreateModalOpen(false)}
            onSave={refreshListAndSummary}
          />
          <EditCouponModal
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            selectedCoupon={selectedCoupon}
            onSave={refreshListAndSummary}
          />
          <CashbackEntryGuideModal
            isOpen={cashbackGuideOpen}
            onClose={() => setCashbackGuideOpen(false)}
            onConfirm={() => {
              setCashbackGuideOpen(false);
              navigate("/order-management/all-orders?status=COMPLETED");
            }}
          />
        </>
      )}
    </div>
  );
};

export default Coupons;
